import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { api } from '../../shared/utils/api';
import DrillCard from '../../pages/drills/DrillCard';
import LeanDrillSummary from './LeanDrillSummary';

const RETRY_DELAYS_MS = [500, 1500, 4000];

function getUserIdFromJwt() {
  try {
    const token = localStorage.getItem('sqetrack_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || payload.identity || null;
  } catch {
    return null;
  }
}

async function postAnswerOnce(payload) {
  const result = await api('/api/questions/practice/answer', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  // api() returns the parsed JSON regardless of HTTP status. Treat
  // {ok:false,...} envelopes and missing bodies as failures so retry kicks in.
  if (!result || result.ok === false || result.error) {
    throw new Error((result && result.error) || 'Save failed');
  }
  return result;
}

export default function LeanDrillSession({ config, onExit }) {
  const [questions, setQuestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [pendingRetry, setPendingRetry] = useState(null);
  const questionStartTime = useRef(null);

  useEffect(() => {
    const body = {
      mode: config.mode,
      question_type: 'drill',
      count: config.count || 10,
    };
    if (config.module && config.module !== 'both') body.module = config.module;
    if (config.topicIds && config.topicIds.length > 0) body.topic_ids = config.topicIds;

    api('/api/questions/practice', {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then((data) => {
        if (!data.questions || data.questions.length === 0) {
          setError(
            "No drills available for this selection. Try a different topic or 'Weakest topics' mode.",
          );
          setLoading(false);
          return;
        }
        setQuestions(data.questions);
        setSessionId(data.session_id);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load drills. Please try again.');
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    questionStartTime.current = Date.now();
  }, [currentIdx]);

  const attemptSubmit = useCallback(
    async (selectedLetter, responseTime) => {
      const question = questions[currentIdx];
      if (!question) return;
      const payload = {
        question_id: question.id,
        selected_answer: selectedLetter,
        response_time_seconds: responseTime ? Math.round(responseTime) : null,
        session_id: sessionId,
      };

      setSaving(true);
      setSaveError(null);

      let lastErr = null;
      for (let i = 0; i <= RETRY_DELAYS_MS.length; i++) {
        try {
          const result = await postAnswerOnce(payload);
          setAnswers((prev) => {
            const next = [...prev];
            next[currentIdx] = { ...result, response_time_seconds: responseTime };
            return next;
          });
          setSaving(false);
          setPendingRetry(null);
          return;
        } catch (err) {
          lastErr = err;
          if (i < RETRY_DELAYS_MS.length) {
            await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[i]));
          }
        }
      }

      setSaving(false);
      setSaveError(
        "We couldn't save your answer. Please check your connection and try again.",
      );
      setPendingRetry({ selectedLetter, responseTime });

      try {
        Sentry.captureException(lastErr || new Error('drill_answer_save_failure'), {
          tags: {
            context: 'drill_answer_save_failure',
            question_id: String(question.id),
            session_id: String(sessionId || ''),
            user_id: String(getUserIdFromJwt() || ''),
          },
        });
      } catch {
        // never let Sentry failures break the UI
      }
    },
    [currentIdx, questions, sessionId],
  );

  const submitAnswer = useCallback(
    (selectedLetter) => {
      const responseTime = questionStartTime.current
        ? (Date.now() - questionStartTime.current) / 1000
        : null;
      attemptSubmit(selectedLetter, responseTime);
    },
    [attemptSubmit],
  );

  const retrySave = useCallback(() => {
    if (!pendingRetry) return;
    attemptSubmit(pendingRetry.selectedLetter, pendingRetry.responseTime);
  }, [pendingRetry, attemptSubmit]);

  const goNext = () => {
    if (currentIdx + 1 >= questions.length) {
      setDone(true);
    } else {
      setCurrentIdx((i) => i + 1);
    }
  };

  if (loading) {
    return <div className="text-sm text-text-muted py-sp-10 text-center">Loading drills…</div>;
  }

  if (error) {
    return (
      <div className="bg-bg-card rounded-r-xl border border-border-soft px-sp-6 py-sp-10 text-center">
        <p className="text-base font-semibold text-text-primary mb-sp-2">No drills available</p>
        <p className="text-sm text-text-muted max-w-sm mx-auto mb-sp-4">{error}</p>
        <button
          onClick={() => onExit()}
          className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
        >
          Back to setup
        </button>
      </div>
    );
  }

  if (done) {
    return (
      <LeanDrillSummary
        questions={questions}
        answers={answers}
        sessionId={sessionId}
        onStartAnother={() => onExit()}
      />
    );
  }

  const question = questions[currentIdx];
  const total = questions.length;
  const result = answers[currentIdx] || null;
  const answered = !!result;
  const atEnd = currentIdx + 1 >= total;

  const segments = questions.map((_, i) => {
    const a = answers[i];
    if (i === currentIdx && !a) return 'bg-accent';
    if (a) return a.correct ? 'bg-green' : 'bg-red';
    return 'bg-border-soft';
  });

  return (
    <>
      <div className="flex items-center justify-end mb-sp-3">
        <span className="text-sm font-medium text-text-muted">
          {currentIdx + 1} / {total}
        </span>
      </div>

      <div className="flex gap-[3px] mb-sp-4">
        {segments.map((seg, i) => (
          <div key={i} className={`flex-1 h-[3px] rounded-[2px] ${seg}`} />
        ))}
      </div>

      <DrillCard
        question={question}
        result={result}
        onSelect={submitAnswer}
        onNext={goNext}
        atEnd={atEnd}
        disabled={saving || !!saveError}
      />

      {saving && (
        <p className="text-sm text-text-muted italic mt-sp-2" aria-live="polite">
          Saving…
        </p>
      )}

      {saveError && (
        <div
          className="mt-sp-3 px-sp-4 py-sp-3 rounded-r-md border border-red bg-[rgba(239,68,68,0.05)]"
          role="alert"
        >
          <p className="text-sm text-red mb-sp-2">{saveError}</p>
          <button
            type="button"
            onClick={retrySave}
            className="px-sp-4 py-sp-2 text-sm font-medium bg-red text-white rounded-r-md hover:opacity-90 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!answered && !saving && !saveError && (
        <div className="mt-sp-4 flex justify-end">
          <button
            onClick={() => onExit()}
            className="text-sm text-text-muted hover:text-text-primary transition-colors"
          >
            End session
          </button>
        </div>
      )}
    </>
  );
}
